/** MIDI Sequencer — full-featured piano roll for composing and playing back notes.
 *
 * Features:
 * - Scrollable piano roll grid with note lanes (C2–C6)
 * - Three tool modes: Select (pointer), Draw (pencil), Erase
 * - Select: click to select notes, shift+click for multi-select, drag to move,
 *   shift+drag for marquee selection, double-click to delete
 * - Draw: click to add notes, drag to set duration, double-click to delete
 * - Erase: click on a note to remove it
 * - Playback sends note-on/off to synth via props
 * - Transport controls: play, stop, loop
 * - Quantize grid dropdown (1/4, 1/8, 1/16, 1/32)
 * - Zoom in/out (Ctrl+wheel / pinch) for both axes
 * - Full-screen toggle for the sequencer section
 * - Per-instrument sequencer data tied to activeSynthId
 * - Copy/paste notes (Ctrl+C / Ctrl+V)
 * - Delete/Backspace removes selected notes
 * - Arrow keys move selected notes (shift for octave/whole-note jumps)
 * - Toggleable velocity lane below the piano roll
 * - Synced piano key labels that follow vertical scroll
 */

import {
	BarChart3,
	BookOpen,
	Clipboard,
	Copy,
	Eraser,
	Maximize2,
	Minimize2,
	MousePointer2,
	Pause,
	Pencil,
	Play,
	RotateCcw,
	Save,
	Square,
	Trash2,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PatternPreset } from "../lib/patternPresets.ts";
import {
	BUILT_IN_PATTERNS,
	deleteUserPattern,
	loadUserPatterns,
	saveUserPattern,
} from "../lib/patternPresets.ts";
import type {
	PersistedSequencerMeasures,
	PersistedSequencerNotes,
	PersistedSynthInstance,
} from "../lib/stateStorage.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single note event in the sequencer. */
export type SequencerNote = {
	readonly id: string;
	readonly note: number;
	readonly startBeat: number;
	readonly durationBeats: number;
	readonly velocity: number;
};

/** Props for the Sequencer component. */
export type SequencerProps = {
	/** Send noteOn to a specific synth instance (for cross-synth playback). */
	readonly synthNoteOn: (
		synthId: string,
		note: number,
		velocity?: number,
	) => void;
	/** Send noteOff to a specific synth instance (for cross-synth playback). */
	readonly synthNoteOff: (synthId: string, note: number) => void;
	/** All synth instances (so the sequencer can play back all patterns). */
	readonly synthInstances: readonly PersistedSynthInstance[];
	readonly tempo: number;
	readonly enabled: boolean;
	readonly activeSynthId: string;
	/** Initial sequencer notes loaded from persisted state. */
	readonly initialNotes: PersistedSequencerNotes;
	/** Initial sequencer measures loaded from persisted state (per-synth). */
	readonly initialMeasures: PersistedSequencerMeasures;
	/** Persist sequencer state changes (notes + measures). */
	readonly onStateChange: (
		notes: PersistedSequencerNotes,
		measures: PersistedSequencerMeasures,
	) => void;
};

/** Tool modes for the sequencer. */
type ToolMode = "select" | "draw" | "erase";

/** Quantize option definition. */
type QuantizeOption = {
	readonly label: string;
	readonly subdivisionsPerBeat: number;
};

/** Notes stored per-synth instance. */
type NotesPerSynth = {
	readonly [synthId: string]: readonly SequencerNote[];
};

/** Drag operation: drawing a new note. */
type DragDraw = {
	readonly type: "draw";
	readonly note: number;
	readonly startBeat: number;
	readonly currentBeat: number;
};

/** Drag operation: moving one or more selected notes. */
type DragMove = {
	readonly type: "move";
	readonly noteIds: ReadonlySet<string>;
	readonly startBeat: number;
	readonly startNote: number;
	readonly currentBeat: number;
	readonly currentNote: number;
	readonly originals: ReadonlyMap<
		string,
		{ readonly startBeat: number; readonly note: number }
	>;
};

/** Drag operation: marquee (lasso) selection rectangle. */
type DragMarquee = {
	readonly type: "marquee";
	readonly startX: number;
	readonly startY: number;
	readonly currentX: number;
	readonly currentY: number;
};

/** Drag operation: adjusting a velocity bar. */
type DragVelocity = {
	readonly type: "velocity";
	readonly noteId: string;
};

/** Drag operation: resizing a note from its left or right edge. */
type DragResize = {
	readonly type: "resize";
	readonly noteId: string;
	/** Which edge is being dragged. */
	readonly edge: "left" | "right";
	/** The original start beat of the note. */
	readonly origStartBeat: number;
	/** The original duration of the note. */
	readonly origDuration: number;
	/** Beat position where the drag started. */
	readonly anchorBeat: number;
	/** Current beat position of the cursor. */
	readonly currentBeat: number;
};

/** Union of all drag operations. */
type DragOp = DragDraw | DragMove | DragMarquee | DragVelocity | DragResize;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Lowest note shown in the piano roll. */
const MIN_NOTE = 12 as const; // C0
/** Highest note shown in the piano roll. */
const MAX_NOTE = 84 as const; // C6
/** Total number of note lanes. */
const NOTE_COUNT = MAX_NOTE - MIN_NOTE + 1;
/** Beats per measure (4/4 time). */
const BEATS_PER_MEASURE = 4 as const;
/** Base pixels per beat before zoom. */
const BASE_BEAT_WIDTH = 48 as const;
/** Base pixels per note lane height before zoom. */
const BASE_LANE_HEIGHT = 14 as const;
/** Minimum zoom factor. */
const MIN_ZOOM = 0.5 as const;
/** Maximum zoom factor. */
const MAX_ZOOM = 3.0 as const;
/** Zoom increment per wheel tick. */
const ZOOM_STEP = 0.1 as const;
/** Default velocity for new notes. */
const DEFAULT_NOTE_VELOCITY = 100 as const;
/** Maximum possible MIDI velocity. */
const MAX_VELOCITY = 127 as const;
/** Height of the velocity lane in pixels. */
const VELOCITY_LANE_HEIGHT = 60 as const;
/** Pixel threshold for detecting a note edge hover (for resize). */
const RESIZE_HANDLE_PX = 6 as const;

/** Quantize options in the dropdown. */
const QUANTIZE_OPTIONS: readonly QuantizeOption[] = [
	{ label: "1/4", subdivisionsPerBeat: 1 },
	{ label: "1/8", subdivisionsPerBeat: 2 },
	{ label: "1/16", subdivisionsPerBeat: 4 },
	{ label: "1/32", subdivisionsPerBeat: 8 },
] as const;

/** Note names for labeling. */
const NOTE_NAMES = [
	"C",
	"C#",
	"D",
	"D#",
	"E",
	"F",
	"F#",
	"G",
	"G#",
	"A",
	"A#",
	"B",
] as const;

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/** Get the display name for a MIDI note number. */
function noteName(midi: number): string {
	const octave = Math.floor(midi / 12) - 1;
	return `${NOTE_NAMES[midi % 12]}${octave}`;
}

/** Whether a MIDI note is a black key. */
function isBlackKey(midi: number): boolean {
	const pc = midi % 12;
	return pc === 1 || pc === 3 || pc === 6 || pc === 8 || pc === 10;
}

/** Snap a beat position to the nearest grid subdivision. */
function snapBeat(beat: number, subdivisions: number): number {
	return Math.round(beat * subdivisions) / subdivisions;
}

/** Generate a unique ID for a sequencer note. */
let noteIdCounter = 0;
function nextNoteId(): string {
	noteIdCounter += 1;
	return `seq-${noteIdCounter}-${Date.now()}`;
}

/** Find a note at a specific beat and MIDI note position. */
function findNoteAt(
	notes: readonly SequencerNote[],
	beat: number,
	midiNote: number,
): SequencerNote | undefined {
	return notes.find(
		(n) =>
			n.note === midiNote &&
			beat >= n.startBeat &&
			beat < n.startBeat + n.durationBeats,
	);
}

/** Clamp a number between min and max. */
function clamp(val: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, val));
}

/** Detect which edge (if any) of a note the cursor is near, in pixel space. */
function detectNoteEdge(
	note: SequencerNote,
	xPx: number,
	beatWidth: number,
): "left" | "right" | null {
	const leftPx = note.startBeat * beatWidth;
	const rightPx = (note.startBeat + note.durationBeats) * beatWidth;
	if (Math.abs(xPx - leftPx) <= RESIZE_HANDLE_PX) return "left";
	if (Math.abs(xPx - rightPx) <= RESIZE_HANDLE_PX) return "right";
	return null;
}

/** Find a note whose edge the cursor is near, using pixel coordinates.
 *  This avoids the snapped-beat issue where findNoteAt misses the edge pixel. */
function findNoteNearEdge(
	notes: readonly SequencerNote[],
	xPx: number,
	midiNote: number,
	beatWidth: number,
): { readonly note: SequencerNote; readonly edge: "left" | "right" } | null {
	for (const n of notes) {
		if (n.note !== midiNote) continue;
		const edge = detectNoteEdge(n, xPx, beatWidth);
		if (edge) return { note: n, edge };
	}
	return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Sequencer({
	synthNoteOn,
	synthNoteOff,
	synthInstances,
	tempo,
	enabled,
	activeSynthId,
	initialNotes,
	initialMeasures,
	onStateChange,
}: SequencerProps) {
	// -- Notes per synth instance --
	const [notesPerSynth, setNotesPerSynth] = useState<NotesPerSynth>(
		() => initialNotes as NotesPerSynth,
	);

	// -- Undo history (per-synth, max 50 snapshots) --
	const MAX_UNDO = 50 as const;
	const undoStackRef = useRef<
		Map<string, readonly (readonly SequencerNote[])[]>
	>(new Map());
	/** Push the current notes for the active synth onto the undo stack. */
	const pushUndo = useCallback(() => {
		const current = notesPerSynthRef.current[activeSynthId] ?? [];
		const stack = undoStackRef.current.get(activeSynthId) ?? [];
		const next = [...stack, current].slice(-MAX_UNDO);
		undoStackRef.current.set(activeSynthId, next);
	}, [activeSynthId, MAX_UNDO]);

	// -- Selection --
	const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
		new Set(),
	);

	// -- Tool --
	const [tool, setTool] = useState<ToolMode>("draw");

	// -- Quantize --
	const [quantizeIdx, setQuantizeIdx] = useState(2); // default 1/16
	const subdivisionsPerBeat =
		QUANTIZE_OPTIONS[quantizeIdx]?.subdivisionsPerBeat ?? 4;

	// -- Zoom --
	const [zoomX, setZoomX] = useState(1);
	const [zoomY, setZoomY] = useState(1);

	// -- Fullscreen --
	const [isFullScreen, setIsFullScreen] = useState(false);

	// -- Velocity lane --
	const [showVelocity, setShowVelocity] = useState(false);

	// -- Transport --
	const [playing, setPlaying] = useState(false);
	const [looping, setLooping] = useState(true);
	const [playbackBeat, setPlaybackBeat] = useState(0);
	/** Per-synth measures map — each sequencer lane can have a different bar count. */
	const [measuresPerSynth, setMeasuresPerSynth] = useState<
		Record<string, number>
	>(() => ({ ...initialMeasures }));

	/** Current active synth's measure count (defaults to 4 for new synths). */
	const measures = measuresPerSynth[activeSynthId] ?? 4;
	const setMeasures = useCallback(
		(updater: number | ((prev: number) => number)) => {
			setMeasuresPerSynth((prev) => {
				const current = prev[activeSynthId] ?? 4;
				const next = typeof updater === "function" ? updater(current) : updater;
				return { ...prev, [activeSynthId]: next };
			});
		},
		[activeSynthId],
	);

	// -- Clipboard --
	const [clipboard, setClipboard] = useState<readonly SequencerNote[]>([]);

	// -- Pattern presets --
	const [showPatternMenu, setShowPatternMenu] = useState(false);
	const [showSaveDialog, setShowSaveDialog] = useState(false);
	const [savePatternName, setSavePatternName] = useState("");
	const [userPatterns, setUserPatterns] = useState<readonly PatternPreset[]>(
		() => loadUserPatterns(),
	);

	// -- Drag operation --
	const [dragOp, setDragOp] = useState<DragOp | null>(null);

	// -- Hover info for pitch tooltip & cursor style --
	const [hoverInfo, setHoverInfo] = useState<{
		readonly pitch: string;
		readonly onNote: boolean;
		readonly edge: "left" | "right" | null;
	} | null>(null);

	// -- Hovered lane (MIDI note) for highlighting --
	const [hoveredLane, setHoveredLane] = useState<number | null>(null);

	// -- Whether Alt is currently held (for duplicate-on-drag) --
	const [altHeld, setAltHeld] = useState(false);

	// -- Refs --
	const gridRef = useRef<HTMLDivElement>(null);
	const keysRef = useRef<HTMLDivElement>(null);
	const gridScrollRef = useRef<HTMLDivElement>(null);
	const velocityScrollRef = useRef<HTMLDivElement>(null);
	const playbackRef = useRef<number | null>(null);
	/** Active playback notes per synth — keyed by synthId. */
	const activePlaybackNotes = useRef<Map<string, Set<number>>>(new Map());
	const dblClickGuard = useRef(false);
	/** Ref to all notes (all synths) for the playback engine. */
	const notesPerSynthRef = useRef(notesPerSynth);

	// -- Derived values --
	const notes = useMemo(
		() => notesPerSynth[activeSynthId] ?? [],
		[notesPerSynth, activeSynthId],
	);
	const notesRef = useRef(notes);
	notesRef.current = notes;
	notesPerSynthRef.current = notesPerSynth;

	// -- Persist sequencer state on change --
	useEffect(() => {
		onStateChange(notesPerSynth, measuresPerSynth);
	}, [notesPerSynth, measuresPerSynth, onStateChange]);

	const beatWidth = BASE_BEAT_WIDTH * zoomX;
	const laneHeight = BASE_LANE_HEIGHT * zoomY;
	const totalBeats = measures * BEATS_PER_MEASURE;
	const gridWidth = totalBeats * beatWidth;
	const gridHeight = NOTE_COUNT * laneHeight;
	const defaultNoteDuration = 1 / subdivisionsPerBeat;

	/** Maximum total beats across all synths — used by the playback engine. */
	const maxPlaybackBeats = useMemo(() => {
		let max = totalBeats;
		for (const m of Object.values(measuresPerSynth)) {
			const beats = m * BEATS_PER_MEASURE;
			if (beats > max) max = beats;
		}
		return max;
	}, [measuresPerSynth, totalBeats]);
	const maxPlaybackBeatsRef = useRef(maxPlaybackBeats);
	maxPlaybackBeatsRef.current = maxPlaybackBeats;

	// -- Notes updater helper (auto-pushes undo) --
	const setNotes = useCallback(
		(updater: (prev: readonly SequencerNote[]) => readonly SequencerNote[]) => {
			pushUndo();
			setNotesPerSynth((prev) => ({
				...prev,
				[activeSynthId]: updater(prev[activeSynthId] ?? []),
			}));
		},
		[activeSynthId, pushUndo],
	);

	// -- Coordinate converters --
	const xToBeat = useCallback(
		(x: number): number =>
			snapBeat(clamp(x / beatWidth, 0, totalBeats), subdivisionsPerBeat),
		[beatWidth, totalBeats, subdivisionsPerBeat],
	);

	const xToBeatRaw = useCallback(
		(x: number): number => clamp(x / beatWidth, 0, totalBeats),
		[beatWidth, totalBeats],
	);

	const yToNote = useCallback(
		(y: number): number => {
			const lane = Math.floor(y / laneHeight);
			return MAX_NOTE - clamp(lane, 0, NOTE_COUNT - 1);
		},
		[laneHeight],
	);

	// -----------------------------------------------------------------------
	// Scroll sync: piano keys <-> grid, velocity lane <-> grid
	// -----------------------------------------------------------------------

	const handleGridScroll = useCallback(() => {
		const el = gridScrollRef.current;
		if (!el) return;
		if (keysRef.current) keysRef.current.scrollTop = el.scrollTop;
		if (velocityScrollRef.current)
			velocityScrollRef.current.scrollLeft = el.scrollLeft;
	}, []);

	// -----------------------------------------------------------------------
	// Mouse handlers
	// -----------------------------------------------------------------------

	/** Get grid-relative coordinates from a mouse event. */
	const gridCoords = useCallback(
		(e: React.MouseEvent): { x: number; y: number } => {
			const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
			return { x: e.clientX - rect.left, y: e.clientY - rect.top };
		},
		[],
	);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			if (!enabled) return;
			if (e.button !== 0) return;
			const { x, y } = gridCoords(e);
			const beat = xToBeat(x);
			const midiNote = yToNote(y);

			// Check for edge resize FIRST using pixel coords (avoids snapped-beat
			// boundary issue where findNoteAt misses the edge).
			if (tool === "select" || tool === "draw") {
				const edgeHit = findNoteNearEdge(notes, x, midiNote, beatWidth);
				if (edgeHit) {
					setDragOp({
						type: "resize",
						noteId: edgeHit.note.id,
						edge: edgeHit.edge,
						origStartBeat: edgeHit.note.startBeat,
						origDuration: edgeHit.note.durationBeats,
						anchorBeat: beat,
						currentBeat: beat,
					});
					if (!selectedIds.has(edgeHit.note.id)) {
						setSelectedIds(new Set([edgeHit.note.id]));
					}
					return;
				}
			}

			const hitNote = findNoteAt(notes, beat, midiNote);

			switch (tool) {
				case "select": {
					if (hitNote) {
						const isSelected = selectedIds.has(hitNote.id);
						if (e.shiftKey) {
							// Toggle selection
							const next = new Set(selectedIds);
							if (isSelected) next.delete(hitNote.id);
							else next.add(hitNote.id);
							setSelectedIds(next);
						} else {
							// If not selected, make it the only selection
							if (!isSelected) {
								setSelectedIds(new Set([hitNote.id]));
							}
							// Start move drag for all selected (including this one)
							const moveIds = isSelected ? selectedIds : new Set([hitNote.id]);
							const originals = new Map<
								string,
								{ startBeat: number; note: number }
							>();
							for (const n of notes) {
								if (moveIds.has(n.id)) {
									originals.set(n.id, {
										startBeat: n.startBeat,
										note: n.note,
									});
								}
							}
							setDragOp({
								type: "move",
								noteIds: moveIds,
								startBeat: beat,
								startNote: midiNote,
								currentBeat: beat,
								currentNote: midiNote,
								originals,
							});
						}
					} else if (e.shiftKey) {
						// Marquee selection
						setDragOp({
							type: "marquee",
							startX: x,
							startY: y,
							currentX: x,
							currentY: y,
						});
					} else {
						// Clicked empty — deselect all
						setSelectedIds(new Set());
					}
					break;
				}
				case "draw": {
					if (hitNote) {
						// Clicking on a note in draw mode does nothing
						// (double-click will handle deletion; edge resize is handled above)
						return;
					}
					// Start drawing
					setDragOp({
						type: "draw",
						note: midiNote,
						startBeat: beat,
						currentBeat: beat,
					});
					break;
				}
				case "erase": {
					if (hitNote) {
						setNotes((prev) => prev.filter((n) => n.id !== hitNote.id));
						setSelectedIds((prev) => {
							if (!prev.has(hitNote.id)) return prev;
							const next = new Set(prev);
							next.delete(hitNote.id);
							return next;
						});
					}
					break;
				}
			}
		},
		[
			enabled,
			tool,
			notes,
			selectedIds,
			gridCoords,
			xToBeat,
			yToNote,
			setNotes,
			beatWidth,
		],
	);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => {
			const { x, y } = gridCoords(e);
			const midiNote = yToNote(y);
			const pitch = noteName(midiNote);

			// Sync alt state from mouse event (more reliable in fullscreen mode)
			setAltHeld(e.altKey);

			// Always update hover info (for tooltip / cursor)
			setHoveredLane(midiNote);
			if (!dragOp) {
				const beat = xToBeatRaw(x);
				// Check for edge first using pixel coords for reliable resize cursor
				const edgeHit = findNoteNearEdge(notes, x, midiNote, beatWidth);
				if (edgeHit) {
					setHoverInfo({
						pitch: noteName(edgeHit.note.note),
						onNote: true,
						edge: edgeHit.edge,
					});
				} else {
					const hitNote = findNoteAt(notes, beat, midiNote);
					if (hitNote) {
						setHoverInfo({
							pitch: noteName(hitNote.note),
							onNote: true,
							edge: null,
						});
					} else {
						setHoverInfo({ pitch, onNote: false, edge: null });
					}
				}
			}

			if (!dragOp) return;

			switch (dragOp.type) {
				case "draw": {
					const beat = xToBeat(x);
					setDragOp({ ...dragOp, currentBeat: beat });
					break;
				}
				case "move": {
					const beat = xToBeat(x);
					setDragOp({
						...dragOp,
						currentBeat: beat,
						currentNote: midiNote,
					});
					break;
				}
				case "marquee": {
					setDragOp({ ...dragOp, currentX: x, currentY: y });
					break;
				}
				case "resize": {
					const beat = xToBeat(x);
					setDragOp({ ...dragOp, currentBeat: beat });
					break;
				}
			}
		},
		[dragOp, gridCoords, xToBeat, xToBeatRaw, yToNote, notes, beatWidth],
	);

	const handleMouseUp = useCallback(() => {
		if (!dragOp) return;

		switch (dragOp.type) {
			case "draw": {
				if (dblClickGuard.current) {
					dblClickGuard.current = false;
					setDragOp(null);
					return;
				}
				const start = Math.min(dragOp.startBeat, dragOp.currentBeat);
				const end = Math.max(dragOp.startBeat, dragOp.currentBeat);
				const duration = end > start ? end - start : defaultNoteDuration;

				const newNote: SequencerNote = {
					id: nextNoteId(),
					note: dragOp.note,
					startBeat: start,
					durationBeats: duration,
					velocity: DEFAULT_NOTE_VELOCITY,
				};
				setNotes((prev) => [...prev, newNote]);
				break;
			}
			case "move": {
				const beatDelta = dragOp.currentBeat - dragOp.startBeat;
				const noteDelta = dragOp.currentNote - dragOp.startNote;
				if (beatDelta !== 0 || noteDelta !== 0) {
					if (altHeld) {
						// Alt+drag: duplicate notes at new position
						const duplicated: SequencerNote[] = [];
						for (const n of notes) {
							const orig = dragOp.originals.get(n.id);
							if (!orig) continue;
							const newStart = snapBeat(
								clamp(
									orig.startBeat + beatDelta,
									0,
									totalBeats - n.durationBeats,
								),
								subdivisionsPerBeat,
							);
							const newMidi = clamp(orig.note + noteDelta, MIN_NOTE, MAX_NOTE);
							duplicated.push({
								...n,
								id: nextNoteId(),
								startBeat: newStart,
								note: newMidi,
							});
						}
						setNotes((prev) => [...prev, ...duplicated]);
						setSelectedIds(new Set(duplicated.map((n) => n.id)));
					} else {
						// Normal move
						setNotes((prev) =>
							prev.map((n) => {
								const orig = dragOp.originals.get(n.id);
								if (!orig) return n;
								const newStart = snapBeat(
									clamp(
										orig.startBeat + beatDelta,
										0,
										totalBeats - n.durationBeats,
									),
									subdivisionsPerBeat,
								);
								const newMidi = clamp(
									orig.note + noteDelta,
									MIN_NOTE,
									MAX_NOTE,
								);
								return { ...n, startBeat: newStart, note: newMidi };
							}),
						);
					}
				}
				break;
			}
			case "marquee": {
				// Determine notes inside the marquee rectangle
				const left = Math.min(dragOp.startX, dragOp.currentX);
				const right = Math.max(dragOp.startX, dragOp.currentX);
				const top = Math.min(dragOp.startY, dragOp.currentY);
				const bottom = Math.max(dragOp.startY, dragOp.currentY);

				const inside = new Set<string>();
				for (const n of notes) {
					const noteLeft = n.startBeat * beatWidth;
					const noteRight = (n.startBeat + n.durationBeats) * beatWidth;
					const lane = MAX_NOTE - n.note;
					const noteTop = lane * laneHeight;
					const noteBottom = noteTop + laneHeight;

					if (
						noteRight > left &&
						noteLeft < right &&
						noteBottom > top &&
						noteTop < bottom
					) {
						inside.add(n.id);
					}
				}
				setSelectedIds(inside);
				break;
			}
			case "resize": {
				const beatDelta = dragOp.currentBeat - dragOp.anchorBeat;
				setNotes((prev) =>
					prev.map((n) => {
						if (n.id !== dragOp.noteId) return n;
						const minDur = 1 / subdivisionsPerBeat;
						if (dragOp.edge === "right") {
							const newDur = Math.max(dragOp.origDuration + beatDelta, minDur);
							return {
								...n,
								durationBeats: snapBeat(newDur, subdivisionsPerBeat),
							};
						}
						// left edge
						const rawStart = dragOp.origStartBeat + beatDelta;
						const maxStart =
							dragOp.origStartBeat + dragOp.origDuration - minDur;
						const newStart = snapBeat(
							clamp(rawStart, 0, maxStart),
							subdivisionsPerBeat,
						);
						const newDur =
							dragOp.origStartBeat + dragOp.origDuration - newStart;
						return {
							...n,
							startBeat: newStart,
							durationBeats: Math.max(newDur, minDur),
						};
					}),
				);
				break;
			}
		}
		setDragOp(null);
	}, [
		dragOp,
		altHeld,
		notes,
		beatWidth,
		laneHeight,
		totalBeats,
		subdivisionsPerBeat,
		defaultNoteDuration,
		setNotes,
	]);

	/** Double-click on a note to remove it (works in select and draw modes). */
	const handleDoubleClick = useCallback(
		(e: React.MouseEvent) => {
			if (!enabled || tool === "erase") return;
			const { x, y } = gridCoords(e);
			const beat = xToBeat(x);
			const midiNote = yToNote(y);
			const hitNote = findNoteAt(notes, beat, midiNote);
			if (hitNote) {
				dblClickGuard.current = true;
				setNotes((prev) => prev.filter((n) => n.id !== hitNote.id));
				setSelectedIds((prev) => {
					if (!prev.has(hitNote.id)) return prev;
					const next = new Set(prev);
					next.delete(hitNote.id);
					return next;
				});
			}
		},
		[enabled, tool, notes, gridCoords, xToBeat, yToNote, setNotes],
	);

	// -----------------------------------------------------------------------
	// Wheel handler — zoom (ctrl/meta + wheel) or normal scroll pass-through
	// -----------------------------------------------------------------------

	const handleWheel = useCallback((e: React.WheelEvent) => {
		if (e.ctrlKey || e.metaKey) {
			e.preventDefault();
			const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
			setZoomX((prev) => clamp(prev + delta, MIN_ZOOM, MAX_ZOOM));
			setZoomY((prev) => clamp(prev + delta, MIN_ZOOM, MAX_ZOOM));
		}
	}, []);

	// -----------------------------------------------------------------------
	// Velocity lane mouse handlers
	// -----------------------------------------------------------------------

	const handleVelocityMouseDown = useCallback(
		(e: React.MouseEvent) => {
			if (!enabled) return;
			const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
			const x = e.clientX - rect.left;
			const beat = xToBeatRaw(x);

			const hitNote = notes.find(
				(n) => beat >= n.startBeat && beat < n.startBeat + n.durationBeats,
			);
			if (hitNote) {
				const y = e.clientY - rect.top;
				const newVelocity = Math.round(
					clamp(MAX_VELOCITY * (1 - y / VELOCITY_LANE_HEIGHT), 1, MAX_VELOCITY),
				);
				const delta = newVelocity - hitNote.velocity;
				const isSelected = selectedIds.has(hitNote.id);
				setNotes((prev) =>
					prev.map((n) => {
						if (n.id === hitNote.id) return { ...n, velocity: newVelocity };
						// If the hit note is selected, apply the same delta to all selected notes
						if (isSelected && selectedIds.has(n.id)) {
							return {
								...n,
								velocity: Math.round(
									clamp(n.velocity + delta, 1, MAX_VELOCITY),
								),
							};
						}
						return n;
					}),
				);
				setDragOp({ type: "velocity", noteId: hitNote.id });
			}
		},
		[enabled, notes, selectedIds, xToBeatRaw, setNotes],
	);

	const handleVelocityMouseMove = useCallback(
		(e: React.MouseEvent) => {
			if (!dragOp || dragOp.type !== "velocity") return;
			const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
			const y = e.clientY - rect.top;
			const newVelocity = Math.round(
				clamp(MAX_VELOCITY * (1 - y / VELOCITY_LANE_HEIGHT), 1, MAX_VELOCITY),
			);
			const targetNote = notes.find((n) => n.id === dragOp.noteId);
			const oldVelocity = targetNote?.velocity ?? newVelocity;
			const delta = newVelocity - oldVelocity;
			const isSelected = selectedIds.has(dragOp.noteId);
			setNotes((prev) =>
				prev.map((n) => {
					if (n.id === dragOp.noteId) return { ...n, velocity: newVelocity };
					if (isSelected && selectedIds.has(n.id)) {
						return {
							...n,
							velocity: Math.round(clamp(n.velocity + delta, 1, MAX_VELOCITY)),
						};
					}
					return n;
				}),
			);
		},
		[dragOp, notes, selectedIds, setNotes],
	);

	const handleVelocityMouseUp = useCallback(() => {
		if (dragOp?.type === "velocity") setDragOp(null);
	}, [dragOp]);

	// -----------------------------------------------------------------------
	// Keyboard shortcuts
	// -----------------------------------------------------------------------

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			const tag = (e.target as HTMLElement)?.tagName;
			if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

			const ctrl = e.ctrlKey || e.metaKey;

			// Escape exits fullscreen
			if (e.key === "Escape") {
				setIsFullScreen(false);
				return;
			}

			// Undo
			if (ctrl && e.key === "z") {
				e.preventDefault();
				const stack = undoStackRef.current.get(activeSynthId);
				if (stack && stack.length > 0) {
					const prev = stack[stack.length - 1];
					undoStackRef.current.set(activeSynthId, stack.slice(0, -1));
					setNotesPerSynth((nps) => ({
						...nps,
						[activeSynthId]: prev ?? [],
					}));
					setSelectedIds(new Set());
				}
				return;
			}

			// Copy
			if (ctrl && e.key === "c" && selectedIds.size > 0) {
				e.preventDefault();
				const selected = notesRef.current.filter((n) => selectedIds.has(n.id));
				if (selected.length === 0) return;
				const minBeat = Math.min(...selected.map((n) => n.startBeat));
				setClipboard(
					selected.map((n) => ({
						...n,
						startBeat: n.startBeat - minBeat,
					})),
				);
				return;
			}

			// Paste
			if (ctrl && e.key === "v" && clipboard.length > 0) {
				e.preventDefault();
				const pasteAt = snapBeat(playbackBeat, subdivisionsPerBeat);
				const pasted = clipboard.map((n) => ({
					...n,
					id: nextNoteId(),
					startBeat: n.startBeat + pasteAt,
				}));
				setNotes((prev) => [...prev, ...pasted]);
				setSelectedIds(new Set(pasted.map((n) => n.id)));
				return;
			}

			// Delete / Backspace
			if (
				(e.key === "Delete" || e.key === "Backspace") &&
				selectedIds.size > 0
			) {
				e.preventDefault();
				setNotes((prev) => prev.filter((n) => !selectedIds.has(n.id)));
				setSelectedIds(new Set());
				return;
			}

			// Arrow keys — move selected notes
			if (
				["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key) &&
				selectedIds.size > 0
			) {
				e.preventDefault();
				const large = e.shiftKey;
				let beatDelta = 0;
				let noteDelta = 0;

				switch (e.key) {
					case "ArrowLeft":
						beatDelta = large ? -BEATS_PER_MEASURE : -defaultNoteDuration;
						break;
					case "ArrowRight":
						beatDelta = large ? BEATS_PER_MEASURE : defaultNoteDuration;
						break;
					case "ArrowUp":
						noteDelta = large ? 12 : 1;
						break;
					case "ArrowDown":
						noteDelta = large ? -12 : -1;
						break;
				}

				setNotes((prev) =>
					prev.map((n) => {
						if (!selectedIds.has(n.id)) return n;
						const newStart = snapBeat(
							clamp(n.startBeat + beatDelta, 0, totalBeats - n.durationBeats),
							subdivisionsPerBeat,
						);
						const newMidi = clamp(n.note + noteDelta, MIN_NOTE, MAX_NOTE);
						return { ...n, startBeat: newStart, note: newMidi };
					}),
				);
			}
		};

		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [
		selectedIds,
		clipboard,
		playbackBeat,
		subdivisionsPerBeat,
		defaultNoteDuration,
		totalBeats,
		setNotes,
		activeSynthId,
	]);

	// -----------------------------------------------------------------------
	// Alt key tracking (for Alt+drag to duplicate)
	// -----------------------------------------------------------------------

	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (e.key === "Alt") setAltHeld(true);
		};
		const up = (e: KeyboardEvent) => {
			if (e.key === "Alt") setAltHeld(false);
		};
		// Also track via blur — if the window loses focus while alt is held,
		// reset to prevent a stuck alt state.
		const blur = () => setAltHeld(false);
		window.addEventListener("keydown", down);
		window.addEventListener("keyup", up);
		window.addEventListener("blur", blur);
		return () => {
			window.removeEventListener("keydown", down);
			window.removeEventListener("keyup", up);
			window.removeEventListener("blur", blur);
		};
	}, []);

	// -----------------------------------------------------------------------
	// Transport controls
	// -----------------------------------------------------------------------

	const togglePlay = useCallback(() => {
		if (playing) {
			setPlaying(false);
			// Release all active playback notes across all synths
			for (const [synthId, noteSet] of activePlaybackNotes.current) {
				for (const n of noteSet) synthNoteOff(synthId, n);
				noteSet.clear();
			}
		} else {
			setPlaying(true);
		}
	}, [playing, synthNoteOff]);

	const stop = useCallback(() => {
		setPlaying(false);
		setPlaybackBeat(0);
		for (const [synthId, noteSet] of activePlaybackNotes.current) {
			for (const n of noteSet) synthNoteOff(synthId, n);
			noteSet.clear();
		}
	}, [synthNoteOff]);

	const clearAll = useCallback(() => {
		stop();
		setNotes(() => []);
		setSelectedIds(new Set());
	}, [stop, setNotes]);

	/** Load a pattern preset into the current synth's notes. */
	const loadPattern = useCallback(
		(preset: PatternPreset) => {
			stop();
			// Re-ID all notes so they are unique
			const reIdNotes = preset.notes.map((n) => ({
				...n,
				id: nextNoteId(),
			}));
			setNotes(() => reIdNotes);
			setMeasures(preset.measures);
			setSelectedIds(new Set());
			setShowPatternMenu(false);
		},
		[stop, setNotes, setMeasures],
	);

	/** Save the current notes as a user pattern. */
	const handleSavePattern = useCallback(() => {
		const name = savePatternName.trim();
		if (!name || notes.length === 0) return;
		const updated = saveUserPattern(name, notes, measures);
		setUserPatterns(updated);
		setSavePatternName("");
		setShowSaveDialog(false);
	}, [savePatternName, notes, measures]);

	/** Delete a user pattern. */
	const handleDeleteUserPattern = useCallback((id: string) => {
		const updated = deleteUserPattern(id);
		setUserPatterns(updated);
	}, []);

	const addMeasure = useCallback(
		() => setMeasures((prev) => Math.min(prev + 1, 32)),
		[setMeasures],
	);
	const removeMeasure = useCallback(
		() => setMeasures((prev) => Math.max(prev - 1, 1)),
		[setMeasures],
	);

	// -----------------------------------------------------------------------
	// Playback engine — plays notes from ALL synths, not just the active one
	// -----------------------------------------------------------------------

	useEffect(() => {
		if (!playing || !enabled) return;

		const beatsPerSecond = tempo / 60;
		const tickMs = 1000 / (beatsPerSecond * subdivisionsPerBeat);
		let currentBeat = playbackBeat;

		const tick = () => {
			currentBeat += 1 / subdivisionsPerBeat;

			const loopEnd = maxPlaybackBeatsRef.current;
			if (currentBeat >= loopEnd) {
				if (looping) {
					currentBeat = 0;
					// Release all notes across all synths on loop wrap
					for (const [synthId, noteSet] of activePlaybackNotes.current) {
						for (const n of noteSet) synthNoteOff(synthId, n);
						noteSet.clear();
					}
				} else {
					setPlaying(false);
					for (const [synthId, noteSet] of activePlaybackNotes.current) {
						for (const n of noteSet) synthNoteOff(synthId, n);
						noteSet.clear();
					}
					return;
				}
			}

			const allNotes = notesPerSynthRef.current;
			const prevBeat = currentBeat - 1 / subdivisionsPerBeat;

			// Iterate all synth instances with notes
			for (const inst of synthInstances) {
				const synthNotes = allNotes[inst.id];
				if (!synthNotes || synthNotes.length === 0) continue;

				let noteSet = activePlaybackNotes.current.get(inst.id);
				if (!noteSet) {
					noteSet = new Set();
					activePlaybackNotes.current.set(inst.id, noteSet);
				}

				for (const n of synthNotes) {
					const noteEnd = n.startBeat + n.durationBeats;
					if (prevBeat < n.startBeat && currentBeat >= n.startBeat) {
						synthNoteOn(inst.id, n.note, n.velocity);
						noteSet.add(n.note);
					}
					if (prevBeat < noteEnd && currentBeat >= noteEnd) {
						synthNoteOff(inst.id, n.note);
						noteSet.delete(n.note);
					}
				}
			}

			setPlaybackBeat(currentBeat);
		};

		const interval = setInterval(tick, tickMs);
		playbackRef.current = interval as unknown as number;

		return () => {
			clearInterval(interval);
			playbackRef.current = null;
		};
	}, [
		playing,
		enabled,
		tempo,
		looping,
		subdivisionsPerBeat,
		synthNoteOn,
		synthNoteOff,
		synthInstances,
		playbackBeat,
	]);

	// -----------------------------------------------------------------------
	// Clear selection when switching synth
	// -----------------------------------------------------------------------

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional effect on synth switch
	useEffect(() => {
		setSelectedIds(new Set());
	}, [activeSynthId]);

	// -----------------------------------------------------------------------
	// Draw preview
	// -----------------------------------------------------------------------

	const drawPreview = useMemo(() => {
		if (!dragOp || dragOp.type !== "draw") return null;
		const start = Math.min(dragOp.startBeat, dragOp.currentBeat);
		const end = Math.max(dragOp.startBeat, dragOp.currentBeat);
		const dur = end > start ? end - start : defaultNoteDuration;
		const lane = MAX_NOTE - dragOp.note;
		return {
			left: start * beatWidth,
			top: lane * laneHeight,
			width: dur * beatWidth,
		};
	}, [dragOp, beatWidth, laneHeight, defaultNoteDuration]);

	// -----------------------------------------------------------------------
	// Move preview deltas
	// -----------------------------------------------------------------------

	const moveDelta = useMemo(() => {
		if (!dragOp || dragOp.type !== "move") return null;
		return {
			beatDelta: dragOp.currentBeat - dragOp.startBeat,
			noteDelta: dragOp.currentNote - dragOp.startNote,
		};
	}, [dragOp]);

	// -----------------------------------------------------------------------
	// Resize preview
	// -----------------------------------------------------------------------

	const resizePreview = useMemo(() => {
		if (!dragOp || dragOp.type !== "resize") return null;
		const beatDelta = dragOp.currentBeat - dragOp.anchorBeat;
		const minDur = 1 / subdivisionsPerBeat;
		if (dragOp.edge === "right") {
			const newDur = Math.max(dragOp.origDuration + beatDelta, minDur);
			return {
				noteId: dragOp.noteId,
				startBeat: dragOp.origStartBeat,
				durationBeats: snapBeat(newDur, subdivisionsPerBeat),
			};
		}
		// left edge
		const rawStart = dragOp.origStartBeat + beatDelta;
		const maxStart = dragOp.origStartBeat + dragOp.origDuration - minDur;
		const newStart = snapBeat(
			clamp(rawStart, 0, maxStart),
			subdivisionsPerBeat,
		);
		const newDur = dragOp.origStartBeat + dragOp.origDuration - newStart;
		return {
			noteId: dragOp.noteId,
			startBeat: newStart,
			durationBeats: Math.max(newDur, minDur),
		};
	}, [dragOp, subdivisionsPerBeat]);

	// -----------------------------------------------------------------------
	// Marquee rect
	// -----------------------------------------------------------------------

	const marqueeRect = useMemo(() => {
		if (!dragOp || dragOp.type !== "marquee") return null;
		return {
			left: Math.min(dragOp.startX, dragOp.currentX),
			top: Math.min(dragOp.startY, dragOp.currentY),
			width: Math.abs(dragOp.currentX - dragOp.startX),
			height: Math.abs(dragOp.currentY - dragOp.startY),
		};
	}, [dragOp]);

	// -----------------------------------------------------------------------
	// Dynamic cursor style for the grid
	// -----------------------------------------------------------------------

	const gridCursor = useMemo(() => {
		// During active drags
		if (dragOp) {
			switch (dragOp.type) {
				case "move":
					return altHeld ? "copy" : "grabbing";
				case "draw":
					return "crosshair";
				case "marquee":
					return "crosshair";
				case "resize":
					return "ew-resize";
				case "velocity":
					return "ns-resize";
			}
		}

		// Hover-based cursor
		if (hoverInfo?.edge) return "ew-resize";

		switch (tool) {
			case "select":
				return hoverInfo?.onNote ? "grab" : "default";
			case "draw":
				return hoverInfo?.onNote ? "default" : "crosshair";
			case "erase":
				return "pointer";
			default:
				return "default";
		}
	}, [tool, dragOp, hoverInfo, altHeld]);

	// -----------------------------------------------------------------------
	// Render helpers
	// -----------------------------------------------------------------------

	const beatLines = useMemo(() => {
		const lines: {
			readonly position: number;
			readonly kind: "measure" | "beat" | "subdivision";
		}[] = [];
		const step = 1 / subdivisionsPerBeat;
		const total = totalBeats / step;
		for (let i = 0; i <= total; i++) {
			const pos = i * step;
			const isMeasure = Number.isInteger(pos) && pos % BEATS_PER_MEASURE === 0;
			const isBeat = !isMeasure && Number.isInteger(pos);
			lines.push({
				position: pos,
				kind: isMeasure ? "measure" : isBeat ? "beat" : "subdivision",
			});
		}
		return lines;
	}, [totalBeats, subdivisionsPerBeat]);

	const lanes = useMemo(
		() =>
			Array.from({ length: NOTE_COUNT }, (_, i) => {
				const midi = MAX_NOTE - i;
				return { midi, black: isBlackKey(midi), index: i };
			}),
		[],
	);

	const keyList = useMemo(
		() =>
			Array.from({ length: NOTE_COUNT }, (_, i) => {
				const midi = MAX_NOTE - i;
				return { midi, black: isBlackKey(midi) };
			}),
		[],
	);

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<div className={`sequencer ${isFullScreen ? "sequencer-fullscreen" : ""}`}>
			{/* Transport bar */}
			<div className="seq-transport">
				<div className="seq-transport-left">
					<button
						type="button"
						className={`seq-btn ${playing ? "active" : ""}`}
						onClick={togglePlay}
						title={playing ? "Pause" : "Play"}
						disabled={!enabled}
					>
						{playing ? <Pause size={12} /> : <Play size={12} />}
					</button>
					<button
						type="button"
						className="seq-btn"
						onClick={stop}
						title="Stop"
						disabled={!enabled}
					>
						<Square size={12} />
					</button>
					<button
						type="button"
						className={`seq-btn ${looping ? "active" : ""}`}
						onClick={() => setLooping((l) => !l)}
						title="Loop"
					>
						<RotateCcw size={12} />
					</button>
					<span className="seq-beat-display">
						{Math.floor(playbackBeat / BEATS_PER_MEASURE) + 1}:
						{(Math.floor(playbackBeat) % BEATS_PER_MEASURE) + 1}
					</span>
				</div>

				<div className="seq-transport-right">
					{/* Tool buttons */}
					<button
						type="button"
						className={`seq-tool-btn ${tool === "select" ? "active" : ""}`}
						onClick={() => setTool("select")}
						title="Select / Move (V)"
					>
						<MousePointer2 size={11} />
					</button>
					<button
						type="button"
						className={`seq-tool-btn ${tool === "draw" ? "active" : ""}`}
						onClick={() => setTool("draw")}
						title="Draw notes (B)"
					>
						<Pencil size={11} />
					</button>
					<button
						type="button"
						className={`seq-tool-btn ${tool === "erase" ? "active" : ""}`}
						onClick={() => setTool("erase")}
						title="Erase notes (E)"
					>
						<Eraser size={11} />
					</button>

					<span className="seq-separator" />

					{/* Quantize dropdown */}
					<label className="seq-quantize-label" htmlFor="seq-quantize">
						Grid
					</label>
					<select
						id="seq-quantize"
						className="seq-quantize-select"
						value={quantizeIdx}
						onChange={(e) => setQuantizeIdx(Number(e.target.value))}
					>
						{QUANTIZE_OPTIONS.map((opt, idx) => (
							<option key={opt.label} value={idx}>
								{opt.label}
							</option>
						))}
					</select>

					<span className="seq-separator" />

					{/* Zoom display */}
					<span className="seq-zoom-display" title="Ctrl+Scroll to zoom">
						{Math.round(zoomX * 100)}%
					</span>

					<span className="seq-separator" />

					{/* Copy / Paste */}
					<button
						type="button"
						className="seq-btn seq-btn-sm"
						onClick={() => {
							const selected = notes.filter((n) => selectedIds.has(n.id));
							if (selected.length === 0) return;
							const minBeat = Math.min(...selected.map((n) => n.startBeat));
							setClipboard(
								selected.map((n) => ({
									...n,
									startBeat: n.startBeat - minBeat,
								})),
							);
						}}
						title="Copy selected (Ctrl+C)"
						disabled={selectedIds.size === 0}
					>
						<Copy size={10} />
					</button>
					<button
						type="button"
						className="seq-btn seq-btn-sm"
						onClick={() => {
							if (clipboard.length === 0) return;
							const pasteAt = snapBeat(playbackBeat, subdivisionsPerBeat);
							const pasted = clipboard.map((n) => ({
								...n,
								id: nextNoteId(),
								startBeat: n.startBeat + pasteAt,
							}));
							setNotes((prev) => [...prev, ...pasted]);
							setSelectedIds(new Set(pasted.map((n) => n.id)));
						}}
						title="Paste at playhead (Ctrl+V)"
						disabled={clipboard.length === 0}
					>
						<Clipboard size={10} />
					</button>

					<span className="seq-separator" />

					{/* Velocity lane toggle */}
					<button
						type="button"
						className={`seq-btn seq-btn-sm ${showVelocity ? "active" : ""}`}
						onClick={() => setShowVelocity((v) => !v)}
						title="Toggle velocity lane"
					>
						<BarChart3 size={10} />
					</button>

					{/* Fullscreen toggle */}
					<button
						type="button"
						className="seq-btn seq-btn-sm"
						onClick={() => setIsFullScreen((f) => !f)}
						title={isFullScreen ? "Exit full screen" : "Full screen"}
					>
						{isFullScreen ? <Minimize2 size={10} /> : <Maximize2 size={10} />}
					</button>

					<span className="seq-separator" />

					{/* Measures */}
					<button
						type="button"
						className="seq-btn seq-btn-sm"
						onClick={removeMeasure}
						title="Remove measure"
						disabled={measures <= 1}
					>
						−
					</button>
					<span className="seq-measure-display">{measures} bars</span>
					<button
						type="button"
						className="seq-btn seq-btn-sm"
						onClick={addMeasure}
						title="Add measure"
						disabled={measures >= 32}
					>
						+
					</button>

					{/* Clear all */}
					<button
						type="button"
						className="seq-btn seq-btn-danger"
						onClick={clearAll}
						title="Clear all notes"
					>
						<Trash2 size={11} />
					</button>

					<span className="seq-separator" />

					{/* Pattern presets */}
					<div className="seq-pattern-presets">
						<button
							type="button"
							className={`seq-btn seq-btn-sm ${showPatternMenu ? "active" : ""}`}
							onClick={() => {
								setShowPatternMenu((v) => !v);
								setShowSaveDialog(false);
							}}
							title="Pattern presets"
						>
							<BookOpen size={10} />
						</button>
						<button
							type="button"
							className="seq-btn seq-btn-sm"
							onClick={() => {
								setShowSaveDialog((v) => !v);
								setShowPatternMenu(false);
							}}
							title="Save current pattern"
							disabled={notes.length === 0}
						>
							<Save size={10} />
						</button>

						{showPatternMenu && (
							<div className="seq-pattern-dropdown">
								<div className="seq-pattern-section">
									<div className="seq-pattern-section-title">Built-in</div>
									{BUILT_IN_PATTERNS.map((p) => (
										<button
											key={p.id}
											type="button"
											className="seq-pattern-item"
											onClick={() => loadPattern(p)}
											title={p.description}
										>
											<span className="seq-pattern-item-name">{p.name}</span>
											<span className="seq-pattern-item-category">
												{p.category}
											</span>
										</button>
									))}
								</div>
								{userPatterns.length > 0 && (
									<div className="seq-pattern-section">
										<div className="seq-pattern-section-title">Saved</div>
										{userPatterns.map((p) => (
											<div key={p.id} className="seq-pattern-item-row">
												<button
													type="button"
													className="seq-pattern-item"
													onClick={() => loadPattern(p)}
													title={p.description}
												>
													<span className="seq-pattern-item-name">
														{p.name}
													</span>
													<span className="seq-pattern-item-category">
														{p.notes.length} notes
													</span>
												</button>
												<button
													type="button"
													className="seq-pattern-delete"
													onClick={() => handleDeleteUserPattern(p.id)}
													title="Delete pattern"
												>
													<X size={8} />
												</button>
											</div>
										))}
									</div>
								)}
							</div>
						)}

						{showSaveDialog && (
							<div className="seq-pattern-save-dialog">
								<input
									type="text"
									className="seq-pattern-save-input"
									placeholder="Pattern name…"
									value={savePatternName}
									onChange={(e) => setSavePatternName(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") handleSavePattern();
										if (e.key === "Escape") setShowSaveDialog(false);
									}}
									// biome-ignore lint/a11y/noAutofocus: intentional focus on open
									autoFocus
								/>
								<button
									type="button"
									className="seq-btn seq-btn-sm"
									onClick={handleSavePattern}
									disabled={!savePatternName.trim() || notes.length === 0}
									title="Save pattern"
								>
									<Save size={10} />
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Piano roll area */}
			<div className="seq-roll-wrapper" onWheel={handleWheel}>
				{/* Piano keys sidebar — scroll synced with grid */}
				<div ref={keysRef} className="seq-keys">
					{keyList.map(({ midi, black }) => (
						<button
							type="button"
							key={midi}
							className={`seq-key ${black ? "black" : "white"} ${hoveredLane === midi ? "hovered" : ""}`}
							style={{ height: laneHeight }}
							onClick={() => {
								// Select all notes in this lane
								const laneNotes = notes.filter((n) => n.note === midi);
								setSelectedIds(new Set(laneNotes.map((n) => n.id)));
							}}
							tabIndex={-1}
							title={`Select all ${noteName(midi)} notes`}
						>
							{midi % 12 === 0 ? noteName(midi) : ""}
						</button>
					))}
				</div>

				{/* Scrollable grid */}
				<div
					ref={gridScrollRef}
					className="seq-grid-scroll"
					onScroll={handleGridScroll}
				>
					<div
						ref={gridRef}
						className="seq-grid"
						style={{ width: gridWidth, height: gridHeight, cursor: gridCursor }}
						onMouseDown={handleMouseDown}
						onMouseMove={handleMouseMove}
						onMouseUp={handleMouseUp}
						onMouseLeave={() => {
							handleMouseUp();
							setHoverInfo(null);
						}}
						onDoubleClick={handleDoubleClick}
						role="application"
						aria-label="Piano roll sequencer grid"
						title={hoverInfo?.pitch ?? ""}
					>
						{/* Beat lines */}
						{beatLines.map(({ position, kind }) => (
							<div
								key={`beat-${String(position)}`}
								className={`seq-beat-line ${kind}`}
								style={{ left: position * beatWidth }}
							/>
						))}

						{/* Lane backgrounds */}
						{lanes.map(({ midi, black, index }) => (
							<div
								key={`lane-${midi}`}
								className={`seq-lane ${black ? "black" : "white"} ${hoveredLane === midi ? "hovered" : ""}`}
								style={{
									top: index * laneHeight,
									height: laneHeight,
								}}
							/>
						))}

						{/* Existing notes */}
						{notes.map((n) => {
							const lane = MAX_NOTE - n.note;
							const isSelected = selectedIds.has(n.id);
							let displayBeat = n.startBeat;
							let displayLane = lane;
							let displayDuration = n.durationBeats;

							// Resize preview
							if (resizePreview && resizePreview.noteId === n.id) {
								displayBeat = resizePreview.startBeat;
								displayDuration = resizePreview.durationBeats;
							} else if (
								moveDelta &&
								dragOp?.type === "move" &&
								(dragOp as DragMove).noteIds.has(n.id)
							) {
								const orig = (dragOp as DragMove).originals.get(n.id);
								if (orig) {
									if (altHeld) {
										// Alt+drag: show original in place, ghost at new position
										// (originals stay; ghost is drawn below)
									} else {
										displayBeat = snapBeat(
											clamp(
												orig.startBeat + moveDelta.beatDelta,
												0,
												totalBeats - n.durationBeats,
											),
											subdivisionsPerBeat,
										);
										const movedMidi = clamp(
											orig.note + moveDelta.noteDelta,
											MIN_NOTE,
											MAX_NOTE,
										);
										displayLane = MAX_NOTE - movedMidi;
									}
								}
							}
							return (
								<div
									key={n.id}
									className={`seq-note ${isSelected ? "selected" : ""}`}
									style={{
										left: displayBeat * beatWidth,
										top: displayLane * laneHeight + 1,
										width: displayDuration * beatWidth,
										height: laneHeight - 2,
									}}
									title={`${noteName(n.note)} vel:${n.velocity} (${n.durationBeats.toFixed(2)} beats)`}
								/>
							);
						})}

						{/* Alt+drag ghost notes (duplicate preview) */}
						{altHeld &&
							moveDelta &&
							dragOp?.type === "move" &&
							notes
								.filter((n) => (dragOp as DragMove).noteIds.has(n.id))
								.map((n) => {
									const orig = (dragOp as DragMove).originals.get(n.id);
									if (!orig) return null;
									const ghostBeat = snapBeat(
										clamp(
											orig.startBeat + moveDelta.beatDelta,
											0,
											totalBeats - n.durationBeats,
										),
										subdivisionsPerBeat,
									);
									const ghostMidi = clamp(
										orig.note + moveDelta.noteDelta,
										MIN_NOTE,
										MAX_NOTE,
									);
									const ghostLane = MAX_NOTE - ghostMidi;
									return (
										<div
											key={`ghost-${n.id}`}
											className="seq-note drawing"
											style={{
												left: ghostBeat * beatWidth,
												top: ghostLane * laneHeight + 1,
												width: n.durationBeats * beatWidth,
												height: laneHeight - 2,
											}}
										/>
									);
								})}

						{/* Drawing preview */}
						{drawPreview && (
							<div
								className="seq-note drawing"
								style={{
									left: drawPreview.left,
									top: drawPreview.top + 1,
									width: drawPreview.width,
									height: laneHeight - 2,
								}}
							/>
						)}

						{/* Marquee rectangle */}
						{marqueeRect && (
							<div
								className="seq-marquee"
								style={{
									left: marqueeRect.left,
									top: marqueeRect.top,
									width: marqueeRect.width,
									height: marqueeRect.height,
								}}
							/>
						)}

						{/* Playback cursor */}
						{playing && (
							<div
								className="seq-cursor"
								style={{
									left: playbackBeat * beatWidth,
								}}
							/>
						)}
					</div>
				</div>
			</div>

			{/* Velocity lane (toggleable) */}
			{showVelocity && (
				<div className="seq-velocity-wrapper">
					<div className="seq-velocity-label">Vel</div>
					{/* biome-ignore lint/a11y/noStaticElementInteractions: velocity lane requires mouse interaction */}
					<div
						ref={velocityScrollRef}
						className="seq-velocity-scroll"
						onMouseDown={handleVelocityMouseDown}
						onMouseMove={handleVelocityMouseMove}
						onMouseUp={handleVelocityMouseUp}
						onMouseLeave={handleVelocityMouseUp}
					>
						<div
							className="seq-velocity-lane"
							style={{
								width: gridWidth,
								height: VELOCITY_LANE_HEIGHT,
							}}
						>
							{notes.map((n) => {
								const barHeight =
									(n.velocity / MAX_VELOCITY) * VELOCITY_LANE_HEIGHT;
								const isSelected = selectedIds.has(n.id);
								return (
									<div
										key={n.id}
										className={`seq-velocity-bar ${isSelected ? "selected" : ""}`}
										style={{
											left: n.startBeat * beatWidth,
											width: Math.max(n.durationBeats * beatWidth - 1, 3),
											height: barHeight,
											bottom: 0,
										}}
										title={`vel: ${n.velocity}`}
									/>
								);
							})}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
